/**
 * A custom emoji, still until you point at it.
 *
 * An animated emoji in a busy log is a room full of things moving at once.
 * The still frame is painted into a canvas the first time the picture loads,
 * and the live image is only swapped in while the pointer is over the
 * message - so a conversation sits quiet until you look at something.
 *
 * A GIF cannot be paused: drawing frame one to a canvas is the only way to
 * hold it still, which is why this is not simply a CSS rule.
 */

import { useEffect, useRef, useState } from 'react';
import { fileUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { openEmojiInfo } from './EmojiInfo';
import type { CustomEmoji } from '@/lib/emoji';

export function EmojiImg({ url, label, big, size: given, playing, emoji }: {
  url: string;
  label: string;
  /** Emoji-only messages draw larger. */
  big?: boolean;
  /** An exact height, for surfaces whose text is not body size. */
  size?: number;
  /** True while the pointer is over the message this sits in. */
  playing?: boolean;
  /**
   * The row behind the picture, when there is one.
   *
   * Given it, the emoji becomes something you can press to find out what it
   * is called and whether you may use it. Left out - in a name, a status line
   * or anywhere a button would be in the way - it stays a picture.
   */
  emoji?: CustomEmoji;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [frozen, setFrozen] = useState(false);
  const src = fileUrl(url);
  const size = given ?? (big ? 38 : 22);

  // Paint the first frame once. Only animated formats need it; a PNG is
  // already still, and freezing it would cost a canvas for nothing.
  useEffect(() => {
    if (!/\.(gif|webp|apng)(\?|$)/i.test(url)) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const node = canvas.current;
      if (!node) return;
      node.width = img.naturalWidth;
      node.height = img.naturalHeight;
      try {
        node.getContext('2d')?.drawImage(img, 0, 0);
        setFrozen(true);
      } catch {
        // Tainted or unavailable - fall back to letting it play.
      }
    };
    img.src = src;
  }, [src, url]);

  const showStill = frozen && !playing;

  const Tag = emoji ? 'button' : 'span';

  return (
    // Sat on the baseline in proportion to its own height, so a 14px emoji in
    // a status line and a 38px one in the log both sit where a letter would.
    <Tag
      {...(emoji
        ? {
            type: 'button' as const,
            'aria-label': label + ', show what this one is',
            onClick: (e: React.MouseEvent<HTMLElement>) => openEmojiInfo(emoji, e.currentTarget)
          }
        : {})}
      className={cn(
        'relative inline-block align-baseline',
        emoji && 'cursor-pointer outline-none rounded-[3px] focus-visible:shadow-[0_0_0_2px_var(--accent)]'
      )}
      style={{ width: size, height: size, verticalAlign: Math.round(size * -0.23) }}
    >
      <canvas
        ref={canvas}
        aria-hidden
        className={cn('absolute inset-0 h-full w-full object-contain', showStill ? 'block' : 'hidden')}
      />
      <img
        src={src}
        alt={label}
        title={label}
        loading="lazy"
        decoding="async"
        className={cn('absolute inset-0 h-full w-full object-contain', showStill && 'invisible')}
      />
    </Tag>
  );
}
